import { keyBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import type { HydratedTripLineItem } from './forms/cells/types';
import { INVOICE_LINE_ITEM_TRIP_ID_MARKER } from './forms/constants';
import { useAppDispatch } from 'src/app/store/hooks';
import type {
  InvoiceNestedLineItem,
  InvoiceRead,
  InvoiceTripsRead,
  LineItemTypeEnum,
} from 'src/common/external/bambi-api/bambiApi';
import { useLazyInvoicesTripsListQuery } from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

export interface HydratedInvoice extends Omit<InvoiceRead, 'line_items'> {
  line_items: HydratedTripLineItem[];
}

type UseHydratedInvoice = {
  isLoading: boolean;
  data: HydratedInvoice | null;
  error: string | null;
};

export function useHydratedInvoice(
  invoice: InvoiceRead | null
): UseHydratedInvoice {
  const [isLoading, setLoading] = useState(true);
  const [hydratedInvoice, setHydratedInvoice] = useState<HydratedInvoice>();
  const [error, setError] = useState(null);

  const hydrate = useInvoiceHydrator(invoice);

  useEffect(() => {
    if (!invoice) {
      return;
    }

    // Support for invoices created from trip rows, these invoices are pre-hydrated
    const areLineItemsHydrated = invoice.line_items.some((lineItem) => {
      return lineItem.trip && typeof lineItem.trip !== 'string';
    });

    if (areLineItemsHydrated) {
      setLoading(false);
      setHydratedInvoice(invoice as unknown as HydratedInvoice);
      return;
    }

    setLoading(true);

    hydrate()
      .then((result) => {
        // No items were able to be hydrated
        if (!result) {
          setHydratedInvoice({
            ...invoice,
            line_items: [],
          });
          return;
        }

        // Filter out line items that couldn't be hydrated, can happen when trip is deleted after invoiced
        const lineItems: HydratedTripLineItem[] = [];

        result.forEach((settledItem) => {
          if (!settledItem.hydrated) {
            return;
          }

          lineItems.push(settledItem as HydratedTripLineItem);
        });

        setHydratedInvoice({
          ...invoice,
          line_items: lineItems,
        });
      })
      .catch((e) => {
        setError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, hydrate, invoice]);

  if (!invoice) {
    return {
      isLoading: false,
      data: null,
      error: null,
    };
  }

  return {
    isLoading,
    data: hydratedInvoice ?? null,
    error,
  };
}

interface PricingSummaryLineItemStub extends Omit<
  InvoiceNestedLineItem,
  'line_item_type'
> {
  line_item_type?: LineItemTypeEnum | 'price_summary';
  created_at: string;
}

export function useInvoiceHydrator(invoice: InvoiceRead | null) {
  const [fetchTripsAndSummary] = useLazyInvoicesTripsListQuery();
  const dispatch = useAppDispatch();

  const hydrate = useCallback(async () => {
    if (!invoice) {
      return;
    }

    // Pregenerate list with pricing summary item
    const items: Array<InvoiceNestedLineItem | PricingSummaryLineItemStub> = [];
    invoice.line_items.forEach((item) => {
      items.push(item);

      if (item.line_item_type === 'trip') {
        items.push({
          ...item,
          line_item_type: 'price_summary',
          created_at: new Date().toLocaleString(),
          description: '',
        });
      }
    });

    try {
      const data = await fetchTripsAndSummary({ id: invoice.id }).unwrap();
      const tripMap = keyBy(data, 'id');

      const hydratedItems = items.map((item) => {
        if (!item.trip) {
          return {
            ...item,
            hydrated: false,
          };
        }

        // Possible that the line item contains a trip that wasn't able to be
        // fetched for some reason, so cast as undefined in this case
        const tripResult = tripMap[item.trip] as InvoiceTripsRead | undefined;

        if (!tripResult) {
          return {
            ...item,
            hydrated: false,
          };
        }

        const pricingResult =
          item.line_item_type === 'price_summary'
            ? tripResult.price_summary
            : null;

        return {
          ...item,
          trip: tripResult,
          invoice: invoice.id,
          price_summary: pricingResult,
          hydrated: true,
        };
      });

      // Tripless modifiers get a synthetic trip stub with only `id` populated.
      // This intentionally does not satisfy InvoiceTripsRead — the cast is unsafe
      // but correct at runtime since only trip.id is accessed on these items.
      const hydratedItemsWithModifiers = hydratedItems.map((item) => {
        if (item.trip === null && item.line_item_type === 'modifier') {
          return {
            ...item,
            trip: {
              id: `${item.id}__${INVOICE_LINE_ITEM_TRIP_ID_MARKER}`,
            },
            hydrated: true,
          };
        }

        return item;
      });

      return hydratedItemsWithModifiers as unknown as HydratedTripLineItem[];
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Failed to fetch trip data for invoice',
          description: formatServerError(e),
        })
      );
      return;
    }
  }, [dispatch, fetchTripsAndSummary, invoice]);

  return hydrate;
}
